import { now } from './broadPhase'
import {
  MAX_TRIANGLE_PAIRS_PER_ELEMENT_PAIR,
  type ClashBox,
  type ClashConfig,
  type NarrowElement,
  type NarrowPairResult,
  type NarrowRequest,
  type NarrowResponse,
} from './clashTypes'
import {
  closestPointOnTriangle,
  epsForScale,
  segmentCrossesTriangle,
  segmentTriangleDistance,
  triTriPenetrateInto,
} from './triTri'

// ---------------------------------------------------------------------------
// Narrow-phase ao nivel do ELEMENTO: dois pares de malhas -> um conflito
// (ou nenhum). Um par de elementos da UM resultado, nunca um por
// triangulo: a UI mostra "esta viga contra esta conduta", nao 4 700
// interseccoes de triangulos.
//
// PIPELINE DE UM PAR
//
//  1. Caixas da geometria. Se o afastamento for maior que a folga
//     pedida, acabou (a broad-phase ja filtrou pelas caixas do motor,
//     mas estas sao as da geometria efectivamente recolhida).
//  2. ZONA DE INTERESSE = interseccao das duas caixas, inflada pela
//     folga. So os triangulos que a tocam entram nos ciclos seguintes.
//     E este pre-filtro que torna o custo praticavel: uma conduta a
//     atravessar uma parede leva dezenas de triangulos a jogo, nao os
//     20 mil de cada elemento.
//  3. HARD: pares de triangulos com caixas sobrepostas -> teste de
//     penetracao (ver triTri.ts). Acumula-se o numero de acertos, o
//     centroide dos segmentos de interseccao, a caixa da zona de
//     interseccao e a uniao das caixas dos triangulos que acertaram.
//  4. PROFUNDIDADE (aproximada): sobreposicao minima, por eixo, entre a
//     caixa dos triangulos de A que acertaram e a de B. E a translacao
//     mais curta, ao longo de um eixo coordenado, que separaria essas
//     duas caixas. Serve para ORDENAR por gravidade. NAO e a
//     profundidade de penetracao exacta (essa exigiria EPA ou um
//     Minkowski completo) e sobrestima em pecas oblicuas.
//     Se a profundidade nao chegar a `tolerance`, nao ha conflito hard:
//     e contacto ou ruido de tesselacao.
//  5. CONTENCAO: zero triangulos a cruzarem-se mas uma caixa inteira
//     dentro da outra pode ser um elemento EMBEBIDO (uma manga dentro
//     de um muro). Testa-se por paridade de raio (3 raios, voto por
//     maioria, para nao ser enganado por um raio que passe exactamente
//     numa aresta). Depende de a malha do continente ser fechada; num
//     solido aberto o teste falha para o lado seguro (nao reporta).
//  6. FOLGA: se nao ha hard e `clearance > 0`, distancia minima entre as
//     malhas (exacta ao nivel dos triangulos, com poda pelas caixas).
//
// Tudo neste ficheiro corre no referencial LOCAL do lote (posicoes
// relativas a `origin`), sem three, sem DOM: e o que permite empacotar
// isto num worker de ~10 kB.
// ---------------------------------------------------------------------------

export interface PreparedElement {
  index: number
  positions: Float32Array
  indices: Uint32Array
  triCount: number
  /** 6 numeros por triangulo. */
  triBoxes: Float64Array
  min: [number, number, number]
  max: [number, number, number]
}

/** Prepara caixas por triangulo e a caixa do elemento. */
export function prepareElement(el: NarrowElement): PreparedElement {
  const pos = el.positions
  const idx = el.indices
  const triCount = Math.floor(idx.length / 3)
  const triBoxes = new Float64Array(triCount * 6)
  const min: [number, number, number] = [Infinity, Infinity, Infinity]
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity]
  for (let t = 0; t < triCount; t++) {
    for (let c = 0; c < 3; c++) {
      let lo = Infinity
      let hi = -Infinity
      for (let v = 0; v < 3; v++) {
        const p = pos[idx[t * 3 + v] * 3 + c]
        if (p < lo) lo = p
        if (p > hi) hi = p
      }
      triBoxes[t * 6 + c] = lo
      triBoxes[t * 6 + 3 + c] = hi
      if (lo < min[c]) min[c] = lo
      if (hi > max[c]) max[c] = hi
    }
  }
  return { index: el.index, positions: pos, indices: idx, triCount, triBoxes, min, max }
}

function boxGap3(
  aMin: readonly number[],
  aMax: readonly number[],
  bMin: readonly number[],
  bMax: readonly number[],
): number {
  let sum = 0
  for (let c = 0; c < 3; c++) {
    const d =
      aMin[c] > bMax[c] ? aMin[c] - bMax[c] : bMin[c] > aMax[c] ? bMin[c] - aMax[c] : 0
    sum += d * d
  }
  return sum === 0 ? 0 : Math.sqrt(sum)
}

/** Afastamento entre duas caixas de triangulo, lidas directamente dos
 *  buffers (sem alocar). */
function triBoxGap(
  ta: Float64Array,
  oa: number,
  tb: Float64Array,
  ob: number,
): number {
  let sum = 0
  for (let c = 0; c < 3; c++) {
    const aLo = ta[oa + c]
    const aHi = ta[oa + 3 + c]
    const bLo = tb[ob + c]
    const bHi = tb[ob + 3 + c]
    const d = aLo > bHi ? aLo - bHi : bLo > aHi ? bLo - aHi : 0
    sum += d * d
  }
  return sum === 0 ? 0 : Math.sqrt(sum)
}

/** Triangulos cuja caixa toca a regiao dada. */
function trianglesInRegion(
  el: PreparedElement,
  rMin: readonly number[],
  rMax: readonly number[],
): Uint32Array {
  const keep = new Uint32Array(el.triCount)
  let n = 0
  const tb = el.triBoxes
  for (let t = 0; t < el.triCount; t++) {
    const o = t * 6
    if (
      tb[o] > rMax[0] || tb[o + 3] < rMin[0] ||
      tb[o + 1] > rMax[1] || tb[o + 4] < rMin[1] ||
      tb[o + 2] > rMax[2] || tb[o + 5] < rMin[2]
    ) {
      continue
    }
    keep[n++] = t
  }
  return keep.subarray(0, n)
}

// Rascunhos do ciclo quente.
const _a0 = new Float64Array(3)
const _a1 = new Float64Array(3)
const _a2 = new Float64Array(3)
const _b0 = new Float64Array(3)
const _b1 = new Float64Array(3)
const _b2 = new Float64Array(3)
const _seg = new Float64Array(6)
const _cp = new Float64Array(3)

function readTri(
  el: PreparedElement,
  t: number,
  v0: Float64Array,
  v1: Float64Array,
  v2: Float64Array,
): void {
  const i = el.indices
  const p = el.positions
  const o = t * 3
  const i0 = i[o] * 3
  const i1 = i[o + 1] * 3
  const i2 = i[o + 2] * 3
  v0[0] = p[i0]; v0[1] = p[i0 + 1]; v0[2] = p[i0 + 2]
  v1[0] = p[i1]; v1[1] = p[i1 + 1]; v1[2] = p[i1 + 2]
  v2[0] = p[i2]; v2[1] = p[i2 + 1]; v2[2] = p[i2 + 2]
}

/** Ponto medio dos vertices de um elemento (aproximacao barata do
 *  interior, usada no teste de contencao). */
function vertexCentroid(el: PreparedElement): [number, number, number] {
  const p = el.positions
  const n = Math.floor(p.length / 3)
  if (n === 0) return [0, 0, 0]
  let x = 0
  let y = 0
  let z = 0
  for (let i = 0; i < n; i++) {
    x += p[i * 3]
    y += p[i * 3 + 1]
    z += p[i * 3 + 2]
  }
  return [x / n, y / n, z / n]
}

const _rayEnd = new Float64Array(3)

/** O ponto esta dentro da malha? Paridade de travessias em tres raios
 *  alinhados aos eixos, voto por maioria. Os raios alinhados permitem
 *  descartar triangulos por duas comparacoes (a caixa do triangulo tem
 *  de conter as duas coordenadas transversais), o que torna o teste
 *  praticavel dentro do ciclo. A maioria protege do raio que passa
 *  exactamente por uma aresta partilhada e conta duas travessias.
 *
 *  Pressupoe malha FECHADA. Num solido aberto o resultado tende a
 *  "fora", que e o lado seguro: perde-se um conflito em vez de
 *  inventar um. */
export function pointInsideMesh(
  point: readonly number[],
  el: PreparedElement,
): boolean {
  const span =
    Math.max(
      el.max[0] - el.min[0],
      el.max[1] - el.min[1],
      el.max[2] - el.min[2],
    ) + 1
  let votes = 0
  let against = 0
  const p = [point[0], point[1], point[2]]
  const tb = el.triBoxes
  for (let axis = 0; axis < 3; axis++) {
    // Maioria com saida antecipada: dois votos do mesmo lado chegam.
    if (votes === 2 || against === 2) break
    const u = (axis + 1) % 3
    const v = (axis + 2) % 3
    _rayEnd[0] = p[0]
    _rayEnd[1] = p[1]
    _rayEnd[2] = p[2]
    _rayEnd[axis] = p[axis] + span * 4
    let crossings = 0
    for (let t = 0; t < el.triCount; t++) {
      const o = t * 6
      if (tb[o + 3 + axis] < p[axis]) continue
      if (tb[o + u] > p[u] || tb[o + 3 + u] < p[u]) continue
      if (tb[o + v] > p[v] || tb[o + 3 + v] < p[v]) continue
      readTri(el, t, _b0, _b1, _b2)
      if (segmentCrossesTriangle(p, _rayEnd, _b0, _b1, _b2, null)) crossings++
    }
    if (crossings % 2 === 1) votes++
    else against++
  }
  return votes >= 2
}

/** Distancia do ponto a superficie da malha (minimo sobre todos os
 *  triangulos). */
function distanceToMesh(point: readonly number[], el: PreparedElement): number {
  let best = Infinity
  for (let t = 0; t < el.triCount; t++) {
    readTri(el, t, _b0, _b1, _b2)
    closestPointOnTriangle(point, _b0, _b1, _b2, _cp)
    const dx = point[0] - _cp[0]
    const dy = point[1] - _cp[1]
    const dz = point[2] - _cp[2]
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
    if (d < best) best = d
  }
  return Number.isFinite(best) ? best : 0
}

function boxOf(
  min: readonly number[],
  max: readonly number[],
): ClashBox {
  return {
    min: [min[0], min[1], min[2]],
    max: [max[0], max[1], max[2]],
  }
}

/** Menor sobreposicao, por eixo, entre duas caixas. Negativa quando as
 *  caixas nem se tocam. */
/** Eixos de separacao considerados no majorante, no maximo. */
const MAX_SEPARATION_AXES = 16

/** Acrescenta a normal unitaria do triangulo a `axes`, se ainda nao
 *  houver uma paralela (a direccao e o que conta, nao o sentido). */
function pushNormal(
  axes: number[],
  v0: Float64Array,
  v1: Float64Array,
  v2: Float64Array,
): void {
  const ux = v1[0] - v0[0]
  const uy = v1[1] - v0[1]
  const uz = v1[2] - v0[2]
  const wx = v2[0] - v0[0]
  const wy = v2[1] - v0[1]
  const wz = v2[2] - v0[2]
  let nx = uy * wz - uz * wy
  let ny = uz * wx - ux * wz
  let nz = ux * wy - uy * wx
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz)
  if (!(len > 0)) return
  nx /= len
  ny /= len
  nz /= len
  for (let i = 0; i < axes.length; i += 3) {
    if (Math.abs(axes[i] * nx + axes[i + 1] * ny + axes[i + 2] * nz) > 0.9999) {
      return
    }
  }
  if (axes.length >= MAX_SEPARATION_AXES * 3) return
  axes.push(nx, ny, nz)
}

/** Menor sobreposicao das projeccoes dos dois elementos (todos os
 *  vertices) sobre os eixos dados. Infinity sem eixos. Transladar um
 *  elemento ao longo de um eixo por esta quantidade separa-os, por isso
 *  e um majorante da profundidade de penetracao. */
function projectedOverlap(
  A: PreparedElement,
  B: PreparedElement,
  axes: readonly number[],
): number {
  let best = Infinity
  for (let i = 0; i < axes.length; i += 3) {
    const ax = axes[i]
    const ay = axes[i + 1]
    const az = axes[i + 2]
    const [aLo, aHi] = projectRange(A.positions, ax, ay, az)
    const [bLo, bHi] = projectRange(B.positions, ax, ay, az)
    const ov = Math.min(aHi, bHi) - Math.max(aLo, bLo)
    if (ov < best) best = ov
  }
  return best < 0 ? 0 : best
}

function projectRange(
  p: Float32Array,
  ax: number,
  ay: number,
  az: number,
): [number, number] {
  let lo = Infinity
  let hi = -Infinity
  for (let v = 0; v + 2 < p.length; v += 3) {
    const d = p[v] * ax + p[v + 1] * ay + p[v + 2] * az
    if (d < lo) lo = d
    if (d > hi) hi = d
  }
  return [lo, hi]
}

function minAxisOverlap(
  aMin: readonly number[],
  aMax: readonly number[],
  bMin: readonly number[],
  bMax: readonly number[],
): number {
  let best = Infinity
  for (let c = 0; c < 3; c++) {
    const ov = Math.min(aMax[c], bMax[c]) - Math.max(aMin[c], bMin[c])
    if (ov < best) best = ov
  }
  return Number.isFinite(best) ? best : 0
}

/** Testa um par de elementos. Devolve o conflito ou null.
 *  Coordenadas de entrada e de saida no referencial do lote. */
export function testElementPair(
  A: PreparedElement,
  B: PreparedElement,
  cfg: ClashConfig,
): NarrowPairResult | null {
  if (A.triCount === 0 || B.triCount === 0) return null
  const clearance = cfg.clearance > 0 ? cfg.clearance : 0
  if (boxGap3(A.min, A.max, B.min, B.max) > clearance) return null

  // Escala das coordenadas do par: fixa a tolerancia geometrica do
  // tri-tri ao ruido do Float32 nesta zona do modelo.
  const scale = Math.max(
    Math.abs(A.min[0]), Math.abs(A.max[0]),
    Math.abs(A.min[1]), Math.abs(A.max[1]),
    Math.abs(A.min[2]), Math.abs(A.max[2]),
    Math.abs(B.min[0]), Math.abs(B.max[0]),
    Math.abs(B.min[1]), Math.abs(B.max[1]),
    Math.abs(B.min[2]), Math.abs(B.max[2]),
  )
  const eps = epsForScale(scale)

  const pad = clearance + eps
  const rMin = [0, 0, 0]
  const rMax = [0, 0, 0]
  for (let c = 0; c < 3; c++) {
    rMin[c] = Math.max(A.min[c], B.min[c]) - pad
    rMax[c] = Math.min(A.max[c], B.max[c]) + pad
    if (rMin[c] > rMax[c]) return null
  }
  const listA = trianglesInRegion(A, rMin, rMax)
  const listB = trianglesInRegion(B, rMin, rMax)

  // --- Passagem hard -------------------------------------------------
  // A penetracao nunca pode passar a sobreposicao das caixas: um ponto
  // dentro das duas caixas esta, por construcao, a menos disso da
  // fronteira. Se a sobreposicao ja nao chega a tolerancia, nao ha hard
  // possivel e poupa-se o ciclo todo. E tambem o que despacha, de
  // imediato, os elementos apenas encostados.
  const boxOverlap = minAxisOverlap(A.min, A.max, B.min, B.max)
  let hits = 0
  let truncated = false
  const iMin: [number, number, number] = [Infinity, Infinity, Infinity]
  const iMax: [number, number, number] = [-Infinity, -Infinity, -Infinity]
  let contact: [number, number, number] | null = null
  /** Normais unitarias (xyz seguidos) dos triangulos que se cruzaram:
   *  eixos candidatos para o majorante da penetracao. */
  const axes: number[] = []

  if (boxOverlap > cfg.tolerance) {
    let sx = 0
    let sy = 0
    let sz = 0
    let budget = MAX_TRIANGLE_PAIRS_PER_ELEMENT_PAIR
    for (let ia = 0; ia < listA.length && !truncated; ia++) {
      const ta = listA[ia]
      const oa = ta * 6
      const tba = A.triBoxes
      readTri(A, ta, _a0, _a1, _a2)
      for (let ib = 0; ib < listB.length; ib++) {
        if (--budget <= 0) {
          truncated = true
          break
        }
        const tb = listB[ib]
        const ob = tb * 6
        const tbb = B.triBoxes
        if (
          tba[oa] > tbb[ob + 3] || tba[oa + 3] < tbb[ob] ||
          tba[oa + 1] > tbb[ob + 4] || tba[oa + 4] < tbb[ob + 1] ||
          tba[oa + 2] > tbb[ob + 5] || tba[oa + 5] < tbb[ob + 2]
        ) {
          continue
        }
        readTri(B, tb, _b0, _b1, _b2)
        if (!triTriPenetrateInto(_a0, _a1, _a2, _b0, _b1, _b2, eps, _seg)) continue
        hits++
        if (axes.length < MAX_SEPARATION_AXES * 3) {
          pushNormal(axes, _a0, _a1, _a2)
          pushNormal(axes, _b0, _b1, _b2)
        }
        sx += (_seg[0] + _seg[3]) / 2
        sy += (_seg[1] + _seg[4]) / 2
        sz += (_seg[2] + _seg[5]) / 2
        for (let c = 0; c < 3; c++) {
          const lo = Math.min(_seg[c], _seg[3 + c])
          const hi = Math.max(_seg[c], _seg[3 + c])
          if (lo < iMin[c]) iMin[c] = lo
          if (hi > iMax[c]) iMax[c] = hi
        }
      }
    }
    if (hits > 0) contact = [sx / hits, sy / hits, sz / hits]

    // Profundidade por sondagem do interior. Vale para os dois casos
    // que o teste de superficie nao mede bem:
    //   - travessia normal: a caixa dos triangulos que acertaram nao
    //     diz nada de util quando so uma face e atravessada;
    //   - encaixe rente (dois elementos com a mesma seccao a sobrepor-se
    //     ao comprido) e elemento embebido: nenhum triangulo se cruza
    //     transversalmente, mas os solidos partilham volume.
    const probes = hits > 0 ? PROBES_WITH_HITS : PROBES_WITHOUT_HITS
    const interior = interiorDepth(A, B, listA, listB, probes)
    let depth = interior ? interior.depth : 0
    if (hits > 0 && depth <= cfg.tolerance) {
      // Malha aberta ou sondagem sem sorte: ha travessia a serio, por
      // isso reporta-se com um MAJORANTE da penetracao. Ja nao e so a
      // sobreposicao das caixas alinhadas aos eixos do mundo: numa peca
      // obliqua essa caixa e enorme e um contacto de 0,5 mm saia como
      // conflito de 10 cm. Junta-se a sobreposicao das projeccoes nas
      // normais dos triangulos que se cruzaram: transladar ao longo de
      // um eixo pela sobreposicao das projeccoes separa sempre os dois
      // elementos, por isso o minimo continua a ser um majorante (nunca
      // perde um conflito acima da tolerancia) e fica justo em pecas
      // rodadas.
      depth = Math.max(depth, Math.min(boxOverlap, projectedOverlap(A, B, axes)))
    }
    if (depth > cfg.tolerance) {
      const point = contact ?? interior?.point ?? centreOfOverlap(A, B)
      const bbox = hits > 0 ? boxOf(iMin, iMax) : overlapBox(A, B)
      return {
        a: A.index,
        b: B.index,
        kind: 'hard',
        depth,
        point,
        bbox,
        triangles: hits,
        evidence: hits > 0 ? 'surface' : 'containment',
        truncated: truncated || undefined,
      }
    }
  }

  // --- Passagem de folga ---------------------------------------------
  if (clearance <= 0) return null
  if (contact) {
    // Contacto abaixo da tolerancia: distancia zero, dentro de qualquer
    // folga pedida.
    return {
      a: A.index,
      b: B.index,
      kind: 'clearance',
      distance: 0,
      point: contact,
      bbox: boxOf(iMin, iMax),
      triangles: 0,
    }
  }
  if (listA.length === 0 || listB.length === 0) return null
  let best = Infinity
  const bestPts = new Float64Array(6)
  const tmp = new Float64Array(6)
  for (let ia = 0; ia < listA.length; ia++) {
    const ta = listA[ia]
    const oa = ta * 6
    const tba = A.triBoxes
    readTri(A, ta, _a0, _a1, _a2)
    for (let ib = 0; ib < listB.length; ib++) {
      const tb = listB[ib]
      const ob = tb * 6
      const tbb = B.triBoxes
      // Poda: se as caixas ja estao mais longe do que o melhor
      // encontrado, o par nao pode melhorar.
      const gap = triBoxGap(tba, oa, tbb, ob)
      if (gap >= best || gap > clearance) continue
      readTri(B, tb, _b0, _b1, _b2)
      let d = segmentTriangleDistance(_a0, _a1, _b0, _b1, _b2, tmp)
      if (d < best) { best = d; bestPts.set(tmp) }
      d = segmentTriangleDistance(_a1, _a2, _b0, _b1, _b2, tmp)
      if (d < best) { best = d; bestPts.set(tmp) }
      d = segmentTriangleDistance(_a2, _a0, _b0, _b1, _b2, tmp)
      if (d < best) { best = d; bestPts.set(tmp) }
      d = segmentTriangleDistance(_b0, _b1, _a0, _a1, _a2, tmp)
      if (d < best) { best = d; swapPts(tmp, bestPts) }
      d = segmentTriangleDistance(_b1, _b2, _a0, _a1, _a2, tmp)
      if (d < best) { best = d; swapPts(tmp, bestPts) }
      d = segmentTriangleDistance(_b2, _b0, _a0, _a1, _a2, tmp)
      if (d < best) { best = d; swapPts(tmp, bestPts) }
    }
  }
  if (!(best <= clearance)) return null
  const point: [number, number, number] = [
    (bestPts[0] + bestPts[3]) / 2,
    (bestPts[1] + bestPts[4]) / 2,
    (bestPts[2] + bestPts[5]) / 2,
  ]
  const gapMin: [number, number, number] = [
    Math.min(bestPts[0], bestPts[3]),
    Math.min(bestPts[1], bestPts[4]),
    Math.min(bestPts[2], bestPts[5]),
  ]
  const gapMax: [number, number, number] = [
    Math.max(bestPts[0], bestPts[3]),
    Math.max(bestPts[1], bestPts[4]),
    Math.max(bestPts[2], bestPts[5]),
  ]
  return {
    a: A.index,
    b: B.index,
    kind: 'clearance',
    distance: best,
    point,
    bbox: boxOf(gapMin, gapMax),
    triangles: 0,
  }
}

function swapPts(src: Float64Array, dst: Float64Array): void {
  dst[0] = src[3]
  dst[1] = src[4]
  dst[2] = src[5]
  dst[3] = src[0]
  dst[4] = src[1]
  dst[5] = src[2]
}

interface InteriorHit {
  depth: number
  point: [number, number, number]
}

/** Pontos de sondagem: centroides de triangulos, espacados ao longo da
 *  lista para nao ficarem todos na mesma face. */
function sampleCentroids(
  el: PreparedElement,
  list: Uint32Array,
  count: number,
): [number, number, number][] {
  const out: [number, number, number][] = []
  if (list.length === 0) {
    if (el.triCount > 0) out.push(vertexCentroid(el))
    return out
  }
  const stride = Math.max(1, Math.ceil(list.length / count))
  for (let i = 0; i < list.length && out.length < count; i += stride) {
    readTri(el, list[i], _a0, _a1, _a2)
    out.push([
      (_a0[0] + _a1[0] + _a2[0]) / 3,
      (_a0[1] + _a1[1] + _a2[1]) / 3,
      (_a0[2] + _a1[2] + _a2[2]) / 3,
    ])
  }
  return out
}

/** Sondas quando ja ha travessia confirmada (poucos pares, vale a pena
 *  gastar) e quando nao ha (todos os pares candidatos passam por aqui,
 *  tem de ser barato). */
const PROBES_WITH_HITS = 12
const PROBES_WITHOUT_HITS = 6
/** Sondas a que se mede a distancia a superficie (o passo caro). */
const MEASURED_PROBES = 8

/** Profundidade por sondagem do interior: quanto e que um ponto da
 *  superficie de um elemento esta MERGULHADO no outro.
 *
 *  E uma APROXIMACAO POR AMOSTRAGEM: mede-se um punhado de centroides
 *  de triangulos, nao o ponto mais fundo. Subestima em pecas complexas
 *  e devolve 0 quando nenhuma amostra cai dentro. Nunca sobrestima, o
 *  que e a direccao certa para nao inventar conflitos. */
function interiorDepth(
  A: PreparedElement,
  B: PreparedElement,
  listA: Uint32Array,
  listB: Uint32Array,
  probes: number,
): InteriorHit | null {
  let best: InteriorHit | null = null
  let measured = 0
  const probe = (
    from: PreparedElement,
    list: Uint32Array,
    into: PreparedElement,
  ): void => {
    for (const s of sampleCentroids(from, list, probes)) {
      if (measured >= MEASURED_PROBES) return
      if (!pointInsideMesh(s, into)) continue
      measured++
      const d = distanceToMesh(s, into)
      if (!best || d > best.depth) best = { depth: d, point: s }
    }
  }
  probe(A, listA, B)
  probe(B, listB, A)
  return best
}

/** Caixa da sobreposicao das duas caixas de elemento. */
function overlapBox(A: PreparedElement, B: PreparedElement): ClashBox {
  const min: [number, number, number] = [0, 0, 0]
  const max: [number, number, number] = [0, 0, 0]
  for (let c = 0; c < 3; c++) {
    min[c] = Math.max(A.min[c], B.min[c])
    max[c] = Math.min(A.max[c], B.max[c])
  }
  return { min, max }
}

function centreOfOverlap(
  A: PreparedElement,
  B: PreparedElement,
): [number, number, number] {
  const b = overlapBox(A, B)
  return [
    (b.min[0] + b.max[0]) / 2,
    (b.min[1] + b.max[1]) / 2,
    (b.min[2] + b.max[2]) / 2,
  ]
}

/** Corre um lote de pares. Puro: nao toca no DOM nem no three, o que
 *  permite usa-lo tal-qual dentro do worker. */
export function narrowPhaseBatch(req: NarrowRequest): NarrowResponse {
  const t0 = now()
  const prepared = new Map<number, PreparedElement>()
  for (const el of req.elements) {
    if (Math.floor(el.indices.length / 3) > req.config.maxTrianglesPerElement) {
      continue
    }
    prepared.set(el.index, prepareElement(el))
  }
  const results: NarrowPairResult[] = []
  let testedPairs = 0
  let missing = 0
  const pairCount = Math.floor(req.pairs.length / 2)
  for (let i = 0; i < pairCount; i++) {
    const A = prepared.get(req.pairs[i * 2])
    const B = prepared.get(req.pairs[i * 2 + 1])
    if (!A || !B) {
      missing++
      continue
    }
    testedPairs++
    const r = testElementPair(A, B, req.config)
    if (r) results.push(r)
  }
  return { results, testedPairs, missing, ms: now() - t0 }
}
